import { useState, useEffect } from 'react'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import IconButton from '@mui/material/IconButton'
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutlineOutlined'
import RadioButtonUnchecked from '@mui/icons-material/RadioButtonUnchecked'
import Divider from '@mui/material/Divider'
import Alert from '@mui/material/Alert'
import ImageWithSkeleton from '../../../../../../components/ImageWithSkeleton.js'
import { useTranslation } from 'react-i18next'

export default function MandatoryGear ({ race, distance }) {
  const { t } = useTranslation('raceGuide')
  const distanceData = race?.raceDistances?.find(r => r.distance === distance) || {}
  const myDistanceGear = distanceData?.mandatoryGear || []
  const { mandatoryGear: allGear } = race || {}

  // State for gear checklist
  const [gearStatus, setGearStatus] = useState({})
  const [dbInitialized, setDbInitialized] = useState(false)
  const [loading, setLoading] = useState(true)

  // Initialize IndexedDB
  useEffect(() => {
    const initDB = async () => {
      try {
        // Check if IndexedDB is supported
        if (!window.indexedDB) {
          console.warn('IndexedDB not supported, using localStorage fallback')
          return
        }

        const request = window.indexedDB.open('MandatoryGearDB', 1)

        request.onerror = (event) => {
          console.error('IndexedDB error:', event.target.error)
        }

        request.onsuccess = (event) => {
          const db = event.target.result
          loadGearStatus(db)
        }

        request.onupgradeneeded = (event) => {
          const db = event.target.result
          if (!db.objectStoreNames.contains('gearStatus')) {
            db.createObjectStore('gearStatus', { keyPath: 'raceDistance' })
          }
        }
      } catch (error) {
        console.error('Error initializing IndexedDB:', error)
      }
    }

    const loadGearStatus = (db) => {
      try {
        const transaction = db.transaction(['gearStatus'], 'readonly')
        const store = transaction.objectStore('gearStatus')
        const request = store.get(distance.toString())

        request.onsuccess = () => {
          if (request.result) {
            setGearStatus(request.result.gearStatus || {})
          } else {
            // Initialize with all gear unchecked
            const initialStatus = {}
            myDistanceGear.forEach(gearKey => {
              initialStatus[gearKey] = false
            })
            setGearStatus(initialStatus)
          }
          setDbInitialized(true)
          setLoading(false)
        }

        request.onerror = () => {
          console.error('Error loading gear status')
          setLoading(false)
        }
      } catch (error) {
        console.error('Error in loadGearStatus:', error)
        setLoading(false)
      }
    }

    initDB()
  }, [distance, myDistanceGear])

  // Save gear status to IndexedDB
  const saveGearStatus = async (updatedStatus) => {
    try {
      if (!window.indexedDB) {
        // Fallback to localStorage if IndexedDB not available
        localStorage.setItem(`gearStatus_${distance}`, JSON.stringify(updatedStatus))
        return
      }

      const request = window.indexedDB.open('MandatoryGearDB', 1)

      request.onsuccess = (event) => {
        const db = event.target.result
        const transaction = db.transaction(['gearStatus'], 'readwrite')
        const store = transaction.objectStore('gearStatus')

        store.put({
          raceDistance: distance.toString(),
          gearStatus: updatedStatus
        })
      }
    } catch (error) {
      console.error('Error saving gear status:', error)
    }
  }

  const handleGearToggle = (gearKey) => {
    const updatedStatus = {
      ...gearStatus,
      [gearKey]: !gearStatus[gearKey]
    }
    setGearStatus(updatedStatus)
    saveGearStatus(updatedStatus)
  }

  // Calculate progress
  const checkedCount = Object.values(gearStatus).filter(status => status).length
  const totalCount = myDistanceGear.length
  const progressPercentage = totalCount > 0 ? Math.round((checkedCount / totalCount) * 100) : 0

  if (loading) {
    return <Typography>{t('gear.loading')}</Typography>
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Typography color='warning' gutterBottom>
        {t('gear.mandatoryIntro')}
      </Typography>
      <Typography variant='body2'>
        {t('gear.trackIntro')}
      </Typography>

      <Alert severity='info' sx={{ mt: 2, mb: 2 }}>
        <Typography variant='body2'>
          {t('gear.progress', { checked: checkedCount, total: totalCount, percent: progressPercentage })}
        </Typography>
      </Alert>

      <List dense={false}>
        {myDistanceGear.map((gearKey, index) => {
          const gearInfo = allGear[gearKey]
          const isChecked = gearStatus[gearKey] || false

          return (
            <Box key={gearKey}>
              <ListItem
                secondaryAction={
                  <IconButton edge='end' onClick={() => handleGearToggle(gearKey)}>
                    {isChecked
                      ? <CheckCircleOutline color='success' fontSize='large' />
                      : <RadioButtonUnchecked color='disabled' fontSize='large' />}
                  </IconButton>
                }
              >
                <ListItemIcon>
                  <ImageWithSkeleton
                    src={`https://d1hi0tbu2kom2c.cloudfront.net/assets/${gearKey}.png`}
                    ratio={1}
                    maxWidth={64}
                    oneSize
                    imageSx={{ backgroundColor: '#ddd' }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={gearInfo || gearKey}
                  primaryTypographyProps={{ fontWeight: 'medium', marginLeft: 2 }}
                />
              </ListItem>
              {index < myDistanceGear.length - 1 && <Divider component='li' />}
            </Box>
          )
        })}
      </List>

      {!dbInitialized && (
        <Alert severity='warning' sx={{ mt: 2 }}>
          {t('gear.fallback')}
        </Alert>
      )}
    </Box>
  )
}
